import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Lock, Mail } from 'lucide-react';
import { useCuentaYPermisos, type ContactoDelDueno } from '../lib/permisosDelEquipo';

/**
 * Lo que ve un MIEMBRO del equipo donde el dueño vería planes — ADR-2026-10-19-permisos-por-miembro-del-equipo.
 *
 * Planes y facturación son sólo del dueño. Cuando un miembro se queda sin créditos (o toca
 * "Ver planes"), en vez del catálogo ve a quién pedírselo: nombre y mail del dueño, con un
 * botón que abre el mail ya escrito y otro para copiar la dirección.
 */

const ASUNTO = 'Necesitamos más créditos en Atlas';
const CUERPO =
  'Hola, me quedé sin créditos en Atlas. ¿Podés aumentar el plan desde Configuración › Suscripción así sigo trabajando? Gracias.';

export function mailtoAlDueno(email: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(ASUNTO)}&body=${encodeURIComponent(CUERPO)}`;
}

/** "Copiar mail" del dueño, con el "Copiado" de un segundo y medio. */
function BotonCopiarMail({ email }: { email: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* el mail igual está a la vista para copiarlo a mano */
    }
  };
  return (
    <button
      type="button"
      onClick={copiar}
      className="inline-flex items-center justify-center gap-1.5 py-2.5 px-5 rounded-lg border border-neutral-200 bg-white text-brand-black/90 text-[15px] font-medium hover:bg-neutral-50"
    >
      {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copiado ? 'Copiado' : 'Copiar mail'}
    </button>
  );
}

/** "Escribirle" + "Copiar mail" (pantalla `SoloElDueno`). Sin mail conocido no pinta nada. */
export function BotonesParaEscribirleAlDueno({ dueno }: { dueno: ContactoDelDueno | null }) {
  if (!dueno?.email) return null;
  return (
    <div className="flex flex-col sm:flex-row gap-2.5">
      <a
        href={mailtoAlDueno(dueno.email)}
        className="inline-flex items-center justify-center gap-1.5 py-2.5 px-5 rounded-lg bg-brand-primary text-white text-[15px] font-medium hover:opacity-90"
      >
        <Mail className="w-4 h-4" /> Escribirle un mail
      </a>
      <BotonCopiarMail email={dueno.email} />
    </div>
  );
}

/** "Comunicate con Mati (mati@x.com) para que aumente el plan." */
export function textoPedileAlDueno(dueno: ContactoDelDueno | null, para = 'para que aumente el plan y sigas usando Atlas'): string {
  if (!dueno) return `Comunicate con el administrador de la empresa ${para}.`;
  return dueno.email
    ? `Comunicate con ${dueno.nombre} (${dueno.email}) ${para}.`
    : `Comunicate con ${dueno.nombre} ${para}.`;
}

export function PedileAlDueno({
  titulo = 'Te quedaste sin créditos',
  onClose,
  embebido = false,
}: {
  titulo?: string;
  onClose: () => void;
  embebido?: boolean;
}) {
  const { dueno, cargando } = useCuentaYPermisos();
  if (cargando) return null;
  const panel = (
    <div
      className={
        embebido
          ? 'w-full'
          : 'fixed inset-0 z-[9999] flex items-center justify-center bg-brand-black/60 p-4 backdrop-blur-sm'
      }
      onClick={embebido ? undefined : onClose}
    >
      <div
        className={`w-full max-w-md rounded-lg bg-surface-card p-6 ${embebido ? 'border border-neutral-200' : 'shadow-md'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal={!embebido}
      >
        <Lock className="w-8 h-8 text-brand-primary mb-3" />
        <h3 className="text-[19px] font-semibold text-brand-black/90">{titulo}</h3>
        <p className="text-[15px] text-brand-black/90 mt-2">{textoPedileAlDueno(dueno)}</p>
        <p className="text-[13px] text-neutral-500 mt-1 mb-5">
          En tu equipo, el plan y la facturación los maneja el administrador de la empresa.
        </p>
        {/* Sólo copiar el mail (pedido de Mati: sin "Escribirle un mail" acá) y Cerrar a la derecha. */}
        <div className="flex items-center gap-2.5">
          {dueno?.email && <BotonCopiarMail email={dueno.email} />}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto py-2.5 px-5 rounded-lg text-[15px] font-medium text-neutral-600 hover:bg-neutral-100"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
  return embebido ? panel : createPortal(panel, document.body);
}

export default PedileAlDueno;
